import {
    Component,
    forwardRef,
    Host,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    SkipSelf,
    ViewChild
} from '@angular/core';

import {ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';

import {Subscription} from 'rxjs';

import {ColorCodesEnum} from 'appShared/enums';
import {BaseComponent, CheckboxControl} from 'appShared/classes';

@Component({
    selector: 'app-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CheckboxComponent),
        multi: true
    }]
})
export class CheckboxComponent
    extends BaseComponent<boolean | number | string, MatCheckboxChange, CheckboxControl>
    implements OnInit, ControlValueAccessor, OnDestroy {

    private initialValue: boolean | number | string;
    private valueFixedOnInit: boolean;
    private onChangeExistsOnInit: boolean;
    private checkedObserverSubscription: Subscription;

    @Input() public disabled: boolean;
    @Input() public fluidBetween: boolean;

    @ViewChild('nativeElement', {static: true}) public nativeElementRef: MatCheckbox;

    constructor(
        @Optional() @Host() @SkipSelf()
        protected controlContainer: ControlContainer
    ) {
        super(controlContainer);
    }

    ngOnInit(): void {
        this.valueFixedOnInit = this.onChangeExistsOnInit = false;

        /**
         * Automatically set/reset control value based on 'checked' control attribute.
         * The following subscriber make relation between control value and its 'checked' attribute.
         */
        this.checkedObserverSubscription = this.asFormsControl().checkedObserver.subscribe((status: boolean) => {
            let value = null;

            if (!this.asFormsControl().expectBooleanValue) {
                value = !status ? null : this.initialValue;
            } else {
                value = status;
            }

            /**
             * If control container doesn't exists, the following component is using with NgModel.
             * Calling 'this.onChange' trigger ngModelChange event.
             */
            if (!this.controlContainer) {
                this.onChange(value);
            } else {
                /**
                 * super.writeValue() is not called, because .setValue() will produce execution of
                 * current writeValue() function, it will execute super.writeValue()
                 */
                const control = this.controlContainer.control.get(this.asFormsControl().name);

                if (control) {
                    control.setValue(value, {emitEvent: false});
                }
            }

            this.checkInvalid();
            this.control.resetErrors();

        });

        this.checkInvalid();
    }

    ngOnDestroy(): void {
        if (this.checkedObserverSubscription) {
            this.checkedObserverSubscription.unsubscribe();
        }
    }

    changeValue(event: MatCheckboxChange): void {
        if (!this.asFormsControl().expectBooleanValue) {
            const value = !event.checked ? null : this.initialValue;

            /**
             * Synchronize parent(string/number) value
             */
            super.writeValue(value);

            /**
             * The UI control value is synchronized
             * with model or reactive form control value.
             *
             *
             * Emit change event always in the end.
             *
             */
            this.onChange(value);
        } else {

            /**
             * Synchronize parent(boolean) value
             */
            super.writeValue(event.checked);

            /**
             * Emit change event always in the end.
             */
            this.onChange(event.checked);
        }

        this.asFormsControl().setChecked(event.checked);

        /**
         * Mark the UI control as visited(changed from user)
         */
        this.onTouched();

        this.checkInvalid();
        this.control.resetErrors();
    }

    /**
     * Override parent writeValue method.
     */
    writeValue(value: boolean | string | number): void {
        if (!this.asFormsControl().expectBooleanValue) {
            value = (typeof value !== 'undefined') ? value : null;

            /** Get initial value on FormControl and NgModel init. */
            if (
                (!this.controlContainer && !this.onChangeExistsOnInit) ||
                (this.controlContainer && !this.valueFixedOnInit)
            ) {
                this.initialValue = value;
            }

            value = !this.asFormsControl().getChecked() ? null : value;
        } else {
            /**
             * When checkbox is use as a toggle the state of 'checked' control flag is disregarded.
             * The checkbox status is based on its value.
             */
            if (typeof value !== 'boolean') {
                value = false;
            }

            this.asFormsControl().setChecked(value as boolean);
        }

        /**
         * Trigger (ngModelChange) and sync NgModel control and component value.
         *
         * Check of onChangeExistsOnInit grantee that this.onChange
         * will be executed only on init in the current function.
         *
         * Note: Execution of this.onChange() will trigger (ngModelChange) event.
         */
        if (!this.controlContainer && this.onChange && !this.onChangeExistsOnInit) {
            this.onChange(value);

            this.onChangeExistsOnInit = true;
        }

        /**
         * Reset value of FormControl on init if the checkbox is not checked.
         */
        if (!this.asFormsControl().getChecked()) {
            /**
             * Initial reset is based on the flag 'this.valueFixedOnInit'.
             * Execution of value reset only on init prevent from recursive execution of 'writeValue()'.
             */
            if (this.controlContainer && !this.valueFixedOnInit) {
                const control = this.controlContainer.control.get(this.asFormsControl().name);

                if (control) {
                    control.setValue(!this.asFormsControl().expectBooleanValue ? null : false, {emitEvent: false});
                }
            }
        }

        this.checkInvalid();
        this.valueFixedOnInit = true;

        super.writeValue(value);
    }

    asFormsControl(): CheckboxControl {
        if (!this.control || !(this.control instanceof CheckboxControl)) {
            this.control = new CheckboxControl({
                name: '',
                value: false,
                checked: false,
                placeholder: '',
                validators: [],
                componentType: 0,
                invalid: false,
                errors: [],
                color: ColorCodesEnum.Primary,
                expectBooleanValue: true
            });
        }

        return (this.control as CheckboxControl);
    }
}
